import { fromPath } from '../../../gardener/sets/mythic';
import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * Tick-Tock's shots (the Chronomancer set's gun, ADR-0160 amended): a spinning brass gear on a brass trail, a burst of
 * steam with cogs thrown out of it at the muzzle, and where it lands springs and cogs flying apart. Outline-free.
 */

/**
 * A brass gear at (cx, cy): `teeth` square teeth `width` px wide from its rim (`root`) out to `out`, turned `turn` of a
 * tooth; its face bright inside a darker ring at the teeth's roots, a glint on its top-left, a hole at its axle (none
 * when small). Null off it.
 */
function cog(
  x: number,
  y: number,
  cx: number,
  cy: number,
  out: number,
  root: number,
  teeth: number,
  width: number,
  turn: number,
): Tone {
  const [dx, dy] = [x - cx, y - cy];
  const r = Math.hypot(dx, dy);
  if (r > out) return null;
  if (r > root) {
    const k = Math.round((Math.atan2(dy, dx) / (Math.PI * 2)) * teeth - turn) + turn;
    const a = (k / teeth) * Math.PI * 2;
    if (Math.abs(-dx * Math.sin(a) + dy * Math.cos(a)) >= width / 2) return null;
    return dx + dy < -out * 0.6 ? 'y' : 'Y';
  }
  if (root >= 3 && r < 1.1) return null;
  if (root >= 3 && r < 2) return 'U';
  if (r > root - 0.9 && root >= 3) return dx + dy < -root ? 'Y' : 'U';
  return dx + dy < -root * 0.55 && r > root * 0.3 ? 'w' : 'y';
}

/** A spring at (cx, cy) along `a` (radians), `len` px: a steel coil, its turns lit. */
function spring(x: number, y: number, cx: number, cy: number, a: number, len: number, turns: number): Tone {
  const [ux, uy] = [Math.cos(a), Math.sin(a)];
  const pts: number[] = [];
  for (let i = 0; i <= turns * 4; i++) {
    const t = i / (turns * 4);
    const side = [0, 1.6, 0, -1.6][i % 4] as number;
    pts.push(cx + ux * (t - 0.5) * len - uy * side, cy + uy * (t - 0.5) * len + ux * side);
  }
  const d = fromPath(x, y, pts);
  return d < 0.5 ? 'w' : d < 0.95 ? '4' : null;
}

// --- The bullet ------------------------------------------------------------------------------------------------------

const gearShot = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const g = cog(x, y, 21, 8, 5.4, 3.7, 6, 2.2, f / 3);
    if (g) return g;
    if (x >= 17) return null;
    // Its trail: a streak of brass thinning and breaking up behind it.
    const t = (17 - x) / 15;
    if (t > 1) return null;
    const d = Math.abs(y - 8);
    if (d < 1.6 * (1 - t) + 0.4 && ((Math.floor(x) + f * 2) % 7 !== 0 || t < 0.3))
      return t < 0.25 ? 'y' : t < 0.6 ? 'Y' : 'U';
    // Sparks flicked off it.
    const spark = [
      [10 - f * 2, 5],
      [7 - f * 2, 11],
      [13 - f, 4],
    ] as const;
    return spark.some(([sx, sy]) => Math.floor(x) === sx && Math.floor(y) === sy) ? 'y' : null;
  });

// --- The muzzle ------------------------------------------------------------------------------------------------------

/** The steam's puffs: [x, y, radius] out of the muzzle (4, 20), at their widest. */
const PUFFS = [
  [11, 20, 4.5],
  [18, 16.5, 5],
  [18, 23.5, 4.5],
  [26, 20, 6],
  [33, 15.5, 4.5],
  [34, 24.5, 4],
] as const;
/** The cogs thrown out: their way (radians) and how far each frame. */
const THROWN = [
  [-0.75, 12],
  [-0.05, 15],
  [0.7, 11],
] as const;

const steamBurst = (f: number) => (c: PixelCanvas) => {
  const grow = [0.55, 0.85, 1][f] as number;
  paint(c, (x, y) => {
    for (const [i, [a, d0]] of THROWN.entries()) {
      const d = d0 + f * 7;
      const g = cog(x, y, 5 + Math.cos(a) * d, 20 + Math.sin(a) * d, 2.6, 1.6, 4, 1.4, f / 4 + i * 0.3);
      if (g) return g === 'w' ? 'y' : g;
    }
    if (f === 0) {
      const flash = Math.hypot(x - 6, y - 20);
      if (flash < 2.6) return 'w';
      if (flash < 4.2) return 'y';
    }
    let best: PaletteKey | null = null;
    for (const [px, py, r] of PUFFS) {
      const [cx, cy] = [4 + (px - 4) * grow, 20 + (py - 20) * grow];
      const k = Math.hypot(x - cx, y - cy) / (r * (0.6 + grow * 0.4));
      if (k >= 1) continue;
      // Thinning as it goes: in its last frame only the puffs' rims, torn.
      if (f === 2 && (k < 0.55 || Math.sin(Math.atan2(y - cy, x - cx) * 3 + px) > 0.4)) continue;
      const tone: PaletteKey = k < 0.45 && f < 2 ? 'w' : k < 0.8 ? '5' : '4';
      if (!best || tone === 'w' || (tone === '5' && best === '4')) best = tone;
    }
    return best;
  });
};

// --- The burst -------------------------------------------------------------------------------------------------------

/** What flies apart: [way (radians), cog or spring, its size]. */
const PARTS = [
  [-1.9, 'cog', 4.4],
  [-0.6, 'spring', 7],
  [0.4, 'cog', 3.4],
  [1.3, 'cog', 4.4],
  [2.3, 'spring', 6],
  [3.1, 'cog', 3.4],
] as const;

const springsAndCogs = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const r = Math.hypot(dx, dy);
    if (f === 0) {
      // It strikes: a white flash in a ring of gold, the gear whole.
      if (r < 3) return 'w';
      if (r < 5) return 'y';
      return cog(x, y, 22, 22, 9, 6.5, 8, 3, 0) ? (r < 7.6 ? null : 'Y') : null;
    }
    const out = [0, 9, 14, 18][f] as number;
    for (const [a, kind, size] of PARTS) {
      const [px, py] = [22 + Math.cos(a) * out, 22 + Math.sin(a) * out];
      const shrink = f === 3 ? 0.75 : 1;
      const t =
        kind === 'cog'
          ? cog(x, y, px, py, size * shrink, size * shrink * 0.7, 6, 1.6, f * 0.37 + a)
          : spring(x, y, px, py, a + f * 0.9, size * shrink, 2);
      if (t) return f === 3 && t === 'w' ? '5' : t;
    }
    // Sparks out ahead of them, and the flash's last ring.
    if (f === 1 && Math.abs(r - 7) < 0.7 && Math.sin(Math.atan2(dy, dx) * 6) > 0.3) return 'y';
    if (f === 2 && Math.abs(r - 19) < 0.6 && Math.sin(Math.atan2(dy, dx) * 9 + 1) > 0.85) return 'Y';
    return null;
  });

// --- A cog for the knock-out -----------------------------------------------------------------------------------------

const spinningCog = (f: number) => (c: PixelCanvas) => paint(c, (x, y) => cog(x, y, 6, 6, 5.4, 3.6, 6, 2.2, f / 4));

export default [
  sprite({
    name: 'ticktock_shot',
    ...shot,
    notes: `Tick-Tock's bullet (ADR-0160 amended): a brass gear the stock pellet's size (six square teeth, a white glint on its
lit face, a dark axle) turning a third of a tooth a frame, a brass streak behind it thinning and breaking up, sparks
flicked off. Head right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: its teeth step unevenly as it turns (6 teeth on 11
px), a spinning blur more than a crisp gear at 1:1.`,
    anims: { idle: { fps: 18, frames: frames3(gearShot) } },
  }),
  sprite({
    name: 'ticktock_muzzle',
    ...muzzle,
    notes: `Tick-Tock's muzzle flash (ADR-0160 amended): a burst of steam out of the muzzle from a white flash, puffing out and
breaking up over 3 frames (its last only the puffs' torn rims), three little cogs thrown out of it. Points right.
Rubric (4×): S4 R4 P5 L4 C4 A4 F4. Weakest: its grey steam is low in contrast on the lane (its white heart carries it).`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(steamBurst) } },
  }),
  sprite({
    name: 'ticktock_burst',
    ...burst,
    notes: `Tick-Tock's hit (ADR-0160 amended): the gear strikes (a white flash in its ring), then flies apart: four cogs and two
steel springs thrown out, turning, sparks ahead of them, gone small. 4 frames. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.
Weakest: the springs read as white squiggles at 1:1.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(springsAndCogs) } },
  }),
  sprite({
    name: 'chronomancer_bit',
    group: 'fx',
    size: [12, 12],
    origin: [6, 6],
    density: 4,
    notes: `A brass cog for the Chronomancer's knock-out (ADR-0160 amended): six square teeth, a glint, a dark axle, turning a
quarter of a tooth a frame (4 frames, seamless). Rubric (4×): S5 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(spinningCog) } },
  }),
];
